import {
  useCancelInvitation,
  useInviteMember,
  useOrganisationMembers,
  useOrganisationResponsables,
  usePendingInvitations,
  useRemoveMember,
  useUpdateMemberRole,
  useUpdateOrganisation,
} from '@/hooks/useOrganisations';
import { useOrganisationsStore } from '@/store/organisations.store';
import { useAuthStore } from '@/store/auth.store';
import {
  FolderScope,
  OrganisationMember,
  OrganisationRole,
} from '@/types/organisation.types';
import { cn } from '@/utils/cn';
import { suggestResponsablesForName } from '@/utils/responsable-matching';
import {
  Building2,
  Camera,
  Check,
  Clock,
  FolderLock,
  Globe,
  Mail,
  MapPin,
  MoreHorizontal,
  Phone,
  Save,
  Search,
  Sparkles,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

const ROLE_LABELS: Record<OrganisationRole, string> = {
  owner: 'Proprietaire',
  admin: 'Administrateur',
  member: 'Membre',
  viewer: 'Lecteur',
};

const ROLE_COLORS: Record<OrganisationRole, string> = {
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  member: 'bg-green-100 text-green-700',
  viewer: 'bg-neutral-100 text-neutral-600',
};

const ROLE_HIERARCHY: Record<OrganisationRole, number> = {
  owner: 4,
  admin: 3,
  member: 2,
  viewer: 1,
};

export function OrganisationSettingsPage() {
  const { currentOrganisation } = useOrganisationsStore();
  const { user: currentUser } = useAuthStore();

  const uuid = currentOrganisation?.uuid || '';
  const { data: members, isLoading: loadingMembers } =
    useOrganisationMembers(uuid);

  // Determine current user's role in this organisation
  const currentMember = members?.find(
    (m) => m.userUuid === currentUser?.id || m.user?.uuid === currentUser?.id
  );
  const myRole = currentMember?.role;
  const membersLoaded = !!members;
  const isAdmin = membersLoaded
    ? !!myRole && ROLE_HIERARCHY[myRole] >= ROLE_HIERARCHY['admin']
    : false;

  const { data: pendingInvitations } = usePendingInvitations(uuid, isAdmin);
  const updateOrg = useUpdateOrganisation();
  const inviteMember = useInviteMember();
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  const cancelInvitation = useCancelInvitation();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [siret, setSiret] = useState('');
  const [website, setWebsite] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [logo, setLogo] = useState('');

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrganisationRole>('member');

  // Membre dont on règle le cloisonnement des dossiers (modal admin)
  const [scopeMember, setScopeMember] = useState<OrganisationMember | null>(
    null
  );

  const [activeTab, setActiveTab] = useState<'general' | 'members'>('general');

  useEffect(() => {
    if (currentOrganisation) {
      setName(currentOrganisation.name);
      setEmail(currentOrganisation.email || '');
      setPhone(currentOrganisation.phone || '');
      setSiret(currentOrganisation.siret || '');
      setWebsite(currentOrganisation.website || '');
      setAddress(currentOrganisation.address || '');
      setCity(currentOrganisation.city || '');
      setPostalCode(currentOrganisation.postalCode || '');
      setCountry(currentOrganisation.country || '');
      setLogo(currentOrganisation.logo || '');
    }
  }, [currentOrganisation]);

  if (!currentOrganisation) {
    return (
      <div className="flex h-64 items-center justify-center text-neutral-500">
        Aucune organisation selectionnee
      </div>
    );
  }

  const hasGeneralChanges =
    name !== currentOrganisation.name ||
    email !== (currentOrganisation.email || '') ||
    phone !== (currentOrganisation.phone || '') ||
    siret !== (currentOrganisation.siret || '') ||
    website !== (currentOrganisation.website || '') ||
    address !== (currentOrganisation.address || '') ||
    city !== (currentOrganisation.city || '') ||
    postalCode !== (currentOrganisation.postalCode || '') ||
    country !== (currentOrganisation.country || '') ||
    logo !== (currentOrganisation.logo || '');

  const handleSave = () => {
    if (!name.trim()) return;
    updateOrg.mutate({
      uuid,
      data: {
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        siret: siret.trim() || undefined,
        website: website.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        country: country.trim() || undefined,
        logo: logo.trim() || undefined,
      },
    });
  };

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    inviteMember.mutate(
      { uuid, data: { email: inviteEmail.trim(), role: inviteRole } },
      {
        onSuccess: () => {
          setInviteEmail('');
          setInviteRole('member');
          setShowInvite(false);
        },
      }
    );
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return; // 2MB max
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogo(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100">
            <Building2 className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Organisation
            </h1>
            <p className="text-sm text-neutral-500">
              {isAdmin
                ? 'Gerez les informations et les membres de votre organisation'
                : 'Consultez les informations de votre organisation'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-200">
        <nav className="-mb-px flex gap-6">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={cn(
              'border-b-2 pb-3 text-sm font-medium transition-colors',
              activeTab === 'general'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700'
            )}
          >
            Informations generales
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('members')}
            className={cn(
              'border-b-2 pb-3 text-sm font-medium transition-colors',
              activeTab === 'members'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700'
            )}
          >
            Membres
            {members && (
              <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
                {members.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Tab: General */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          {/* Profil : Logo + Nom + Slug */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
            <h2 className="mb-5 text-base font-semibold text-neutral-900">
              Profil
            </h2>
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
              {/* Logo */}
              <div className="flex flex-col items-center gap-2">
                <div className="relative">
                  <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-neutral-300 bg-neutral-50">
                    {logo ? (
                      <img
                        src={logo}
                        alt="Logo"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Building2 className="h-10 w-10 text-neutral-300" />
                    )}
                  </div>
                  {isAdmin && (
                    <label className="absolute -right-1 -bottom-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-brand-600 text-white shadow-md transition-colors hover:bg-brand-700">
                      <Camera className="h-3.5 w-3.5" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoFileChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                {isAdmin && (
                  <span className="text-[11px] text-neutral-400">
                    PNG, JPG (max 2 Mo)
                  </span>
                )}
              </div>

              {/* Nom + Slug */}
              <div className="flex-1 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                    Nom de l'organisation
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    readOnly={!isAdmin}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none',
                      isAdmin
                        ? 'border-neutral-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                        : 'border-neutral-200 bg-neutral-50 text-neutral-500'
                    )}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                    Identifiant (slug)
                  </label>
                  <input
                    type="text"
                    value={currentOrganisation.slug}
                    readOnly
                    className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Informations entreprise */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
            <h2 className="mb-5 text-base font-semibold text-neutral-900">
              Informations entreprise
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { label: 'Email', icon: Mail, value: email, setter: setEmail, placeholder: "Adresse email de l'entreprise", type: 'email' },
                { label: 'Téléphone', icon: Phone, value: phone, setter: setPhone, placeholder: 'Numéro professionnel', type: 'tel' },
                { label: 'SIRET', icon: null, value: siret, setter: setSiret, placeholder: 'Numéro SIRET', type: 'text' },
                { label: 'Site web', icon: Globe, value: website, setter: setWebsite, placeholder: "Adresse du site de l'entreprise", type: 'url' },
              ].map(({ label, icon: Icon, value, setter, placeholder, type }) => (
                <div key={label}>
                  <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-neutral-700">
                    {Icon && <Icon className="h-3.5 w-3.5 text-neutral-400" />}
                    {label}
                  </label>
                  <input
                    type={type}
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    readOnly={!isAdmin}
                    placeholder={placeholder}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2 text-sm placeholder:text-neutral-400/50 focus:outline-none',
                      isAdmin
                        ? 'border-neutral-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                        : 'border-neutral-200 bg-neutral-50 text-neutral-500'
                    )}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Adresse */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
            <h2 className="mb-5 flex items-center gap-2 text-base font-semibold text-neutral-900">
              <MapPin className="h-4 w-4 text-neutral-400" />
              Adresse
            </h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                  Adresse
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  readOnly={!isAdmin}
                  placeholder="12 rue de la Paie"
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none',
                    isAdmin
                      ? 'border-neutral-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                      : 'border-neutral-200 bg-neutral-50 text-neutral-500'
                  )}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  { label: 'Code postal', value: postalCode, setter: setPostalCode, placeholder: '75001' },
                  { label: 'Ville', value: city, setter: setCity, placeholder: 'Paris' },
                  { label: 'Pays', value: country, setter: setCountry, placeholder: 'France' },
                ].map(({ label, value, setter, placeholder }) => (
                  <div key={label}>
                    <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                      {label}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => setter(e.target.value)}
                      readOnly={!isAdmin}
                      placeholder={placeholder}
                      className={cn(
                        'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none',
                        isAdmin
                          ? 'border-neutral-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                          : 'border-neutral-200 bg-neutral-50 text-neutral-500'
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Save button - only for admins */}
          {isAdmin && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasGeneralChanges || updateOrg.isPending}
                className="flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {updateOrg.isPending ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tab: Members */}
      {activeTab === 'members' && (
        <div className="space-y-4">
          {/* Header bar */}
          <div className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white px-6 py-4 shadow-sm">
            <div>
              <h2 className="text-base font-semibold text-neutral-900">
                Membres de l'organisation
              </h2>
              <p className="text-sm text-neutral-500">
                {isAdmin
                  ? 'Gerez les acces et les roles des membres'
                  : 'Consultez les membres de l\'organisation'}
              </p>
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowInvite(true)}
                className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
              >
                <UserPlus className="h-4 w-4" />
                Inviter
              </button>
            )}
          </div>

          {/* Members list */}
          {loadingMembers ? (
            <div className="py-12 text-center text-neutral-500">
              Chargement...
            </div>
          ) : (
            <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold tracking-wide text-neutral-500 uppercase">
                      Membre
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold tracking-wide text-neutral-500 uppercase">
                      Role
                    </th>
                    <th className="hidden px-6 py-3.5 text-left text-xs font-semibold tracking-wide text-neutral-500 uppercase sm:table-cell">
                      Rejoint le
                    </th>
                    {isAdmin && (
                      <th className="px-6 py-3.5 text-right text-xs font-semibold tracking-wide text-neutral-500 uppercase">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {members?.map((member) => (
                    <tr
                      key={member.uuid}
                      className="transition-colors hover:bg-neutral-50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700">
                            {(member.user.firstName?.[0] || '').toUpperCase()}
                            {(member.user.lastName?.[0] || '').toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-neutral-900">
                              {member.user.firstName} {member.user.lastName}
                              {(member.userUuid === currentUser?.id || member.user?.uuid === currentUser?.id) && (
                                <span className="ml-1.5 text-xs text-neutral-400">(vous)</span>
                              )}
                            </div>
                            <div className="text-xs text-neutral-500">
                              {member.user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-start gap-1">
                          <span
                            className={cn(
                              'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                              ROLE_COLORS[member.role]
                            )}
                          >
                            {ROLE_LABELS[member.role]}
                          </span>
                          {member.folderScope === 'own' && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                              title={
                                member.responsableAliases &&
                                member.responsableAliases.length > 0
                                  ? `Responsable : ${member.responsableAliases.join(', ')}`
                                  : 'Aucun responsable assigné'
                              }
                            >
                              <FolderLock className="h-3 w-3" />
                              Ses dossiers
                              {member.responsableAliases &&
                                member.responsableAliases.length > 0 && (
                                  <span className="opacity-70">
                                    ({member.responsableAliases.length})
                                  </span>
                                )}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="hidden px-6 py-4 text-neutral-500 sm:table-cell">
                        {member.joinedAt
                          ? new Date(member.joinedAt).toLocaleDateString(
                            'fr-FR'
                          )
                          : '-'}
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 text-right">
                          {member.role !== 'owner' && member.user?.uuid !== currentUser?.id && (
                            <MemberActions
                              member={member}
                              onChangeRole={(role) =>
                                updateRole.mutate({
                                  uuid,
                                  memberUuid: member.uuid,
                                  role,
                                })
                              }
                              onManageScope={() => setScopeMember(member)}
                              onRemove={() =>
                                removeMember.mutate({
                                  uuid,
                                  memberUuid: member.uuid,
                                })
                              }
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pending Invitations - only for admins */}
          {isAdmin && pendingInvitations && pendingInvitations.length > 0 && (
            <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
              <div className="flex items-center gap-2 border-b border-neutral-100 px-6 py-4">
                <Clock className="h-4 w-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-neutral-900">
                  Invitations en attente
                </h3>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                  {pendingInvitations.length}
                </span>
              </div>
              <div className="divide-y divide-neutral-100">
                {pendingInvitations.map((inv) => (
                  <div
                    key={inv.uuid}
                    className="flex items-center justify-between px-6 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-sm font-semibold text-amber-700">
                        <Mail className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-neutral-900">
                          {inv.email}
                        </div>
                        <div className="text-xs text-neutral-500">
                          Expire le{' '}
                          {new Date(inv.expiresAt).toLocaleDateString('fr-FR')}
                          {' - '}
                          <span className="font-medium">
                            {ROLE_LABELS[inv.role]}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        cancelInvitation.mutate({
                          uuid,
                          invitationUuid: inv.uuid,
                        })
                      }
                      disabled={cancelInvitation.isPending}
                      className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      title="Annuler l'invitation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-neutral-900">
                Inviter un membre
              </h3>
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="rounded-lg p-1 text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700">
                  Email
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="collaborateur@exemple.com"
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) =>
                    setInviteRole(e.target.value as OrganisationRole)
                  }
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                >
                  <option value="admin">Administrateur</option>
                  <option value="member">Membre</option>
                  <option value="viewer">Lecteur</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowInvite(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!inviteEmail.trim() || inviteMember.isPending}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {inviteMember.isPending ? 'Envoi...' : 'Inviter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal : cloisonnement des dossiers d'un membre (admin) */}
      {scopeMember && (
        <MemberScopeModal
          orgUuid={uuid}
          member={scopeMember}
          onClose={() => setScopeMember(null)}
        />
      )}
    </div>
  );
}

function MemberActions({
  member,
  onChangeRole,
  onManageScope,
  onRemove,
}: {
  member: { uuid: string; role: OrganisationRole };
  onChangeRole: (role: OrganisationRole) => void;
  onManageScope: () => void;
  onRemove: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const roles: OrganisationRole[] = ['admin', 'member', 'viewer'];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className="fixed z-50 w-48 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg"
          style={{
            top: ref.current
              ? ref.current.getBoundingClientRect().bottom + 4
              : 0,
            left: ref.current
              ? ref.current.getBoundingClientRect().right - 192
              : 0,
          }}
        >
          <div className="border-b border-neutral-100 px-3 py-2 text-xs font-medium text-neutral-500">
            Changer le role
          </div>
          {roles.map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => {
                onChangeRole(role);
                setIsOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-neutral-50',
                member.role === role
                  ? 'font-medium text-brand-600'
                  : 'text-neutral-700'
              )}
            >
              {ROLE_LABELS[role]}
            </button>
          ))}
          <div className="border-t border-neutral-100">
            <button
              type="button"
              onClick={() => {
                onManageScope();
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
            >
              <FolderLock className="h-4 w-4 text-neutral-400" />
              Accès aux dossiers
            </button>
          </div>
          <div className="border-t border-neutral-100">
            <button
              type="button"
              onClick={() => {
                onRemove();
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              Retirer le membre
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const FOLDER_SCOPE_OPTIONS: {
  value: FolderScope;
  title: string;
  description: string;
}[] = [
  {
    value: 'all',
    title: 'Voit tous les dossiers',
    description: "Accès à tous les dossiers de l'organisation.",
  },
  {
    value: 'own',
    title: 'Voit seulement ses dossiers',
    description:
      'Limité aux dossiers dont il est responsable (+ ceux partagés).',
  },
];

/**
 * Modal admin : règle le cloisonnement d'un membre.
 * - folderScope : tous les dossiers / seulement ses dossiers.
 * - responsableAliases : valeurs responsable_dossier assignées au membre,
 *   pré-suggérées d'après son nom (chevauchement de tokens, casse/accents).
 */
function MemberScopeModal({
  orgUuid,
  member,
  onClose,
}: {
  orgUuid: string;
  member: OrganisationMember;
  onClose: () => void;
}) {
  const { data: responsables, isLoading: loadingResponsables } =
    useOrganisationResponsables(orgUuid);
  const updateMember = useUpdateMemberRole();

  const [folderScope, setFolderScope] = useState<FolderScope>(
    member.folderScope === 'own' ? 'own' : 'all'
  );
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(member.responsableAliases ?? [])
  );
  const [search, setSearch] = useState('');

  const memberName = `${member.user.firstName ?? ''} ${member.user.lastName ?? ''}`.trim();

  // Responsables suggérés (ressemblant au nom du membre)
  const suggestedSet = useMemo(() => {
    const names = (responsables ?? []).map((r) => r.responsable);
    return new Set(suggestResponsablesForName(memberName, names));
  }, [responsables, memberName]);

  // Pré-sélection automatique des suggestions si le membre n'a encore aucun alias
  // (uniquement quand on passe en "own" et que rien n'est sélectionné). L'admin
  // garde la main : il confirme/ajuste avant d'enregistrer.
  const didAutoSuggest = useRef(false);
  useEffect(() => {
    if (didAutoSuggest.current) return;
    if (loadingResponsables) return;
    const hasExisting = (member.responsableAliases ?? []).length > 0;
    if (!hasExisting && suggestedSet.size > 0) {
      setSelected(new Set(suggestedSet));
    }
    didAutoSuggest.current = true;
  }, [loadingResponsables, suggestedSet, member.responsableAliases]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const applySuggestions = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      suggestedSet.forEach((name) => next.add(name));
      return next;
    });
  };

  const filteredResponsables = useMemo(() => {
    const list = responsables ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) => r.responsable.toLowerCase().includes(q));
  }, [responsables, search]);

  const handleSave = () => {
    updateMember.mutate(
      {
        uuid: orgUuid,
        memberUuid: member.uuid,
        folderScope,
        // On envoie toujours les alias : ils restent utiles si l'admin rebascule
        // plus tard en "own". Le back nettoie/déduplique.
        responsableAliases: Array.from(selected),
      },
      { onSuccess: onClose }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl border border-neutral-200 bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-neutral-100 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100">
              <FolderLock className="h-4 w-4 text-brand-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-neutral-900">
                Accès aux dossiers
              </h3>
              <p className="text-xs text-neutral-500">
                {member.user.firstName} {member.user.lastName}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-neutral-400 hover:text-neutral-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          {/* Périmètre */}
          <div className="space-y-2">
            {FOLDER_SCOPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFolderScope(option.value)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                  folderScope === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border',
                    folderScope === option.value
                      ? 'border-brand-600 bg-brand-600'
                      : 'border-neutral-300'
                  )}
                >
                  {folderScope === option.value && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </span>
                <span>
                  <span className="block text-sm font-medium text-neutral-900">
                    {option.title}
                  </span>
                  <span className="block text-xs text-neutral-500">
                    {option.description}
                  </span>
                </span>
              </button>
            ))}
          </div>

          {/* Responsables (alias) — pertinent surtout en "own" */}
          {folderScope === 'own' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-neutral-700">
                  Responsables assignés
                  {selected.size > 0 && (
                    <span className="ml-1 text-xs font-normal text-neutral-400">
                      ({selected.size})
                    </span>
                  )}
                </label>
                {suggestedSet.size > 0 && (
                  <button
                    type="button"
                    onClick={applySuggestions}
                    className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-100"
                  >
                    <Sparkles className="h-3 w-3" />
                    Appliquer les suggestions
                  </button>
                )}
              </div>

              {/* Pré-suggestion d'après le nom du membre */}
              {suggestedSet.size > 0 && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  Suggéré d'après le nom du membre — vérifiez puis ajustez.
                </p>
              )}

              {/* Recherche */}
              <div className="relative">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un responsable"
                  className="w-full rounded-lg border border-neutral-300 py-2 pr-3 pl-9 text-sm placeholder:text-neutral-400/60 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                />
              </div>

              {/* Liste des responsables */}
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-neutral-200 p-1">
                {loadingResponsables ? (
                  <p className="px-3 py-4 text-center text-sm text-neutral-400">
                    Chargement...
                  </p>
                ) : filteredResponsables.length === 0 ? (
                  <p className="px-3 py-4 text-center text-sm text-neutral-400">
                    {(responsables ?? []).length === 0
                      ? 'Aucun responsable de dossier trouvé'
                      : 'Aucun résultat'}
                  </p>
                ) : (
                  filteredResponsables.map((r) => {
                    const isChecked = selected.has(r.responsable);
                    const isSuggested = suggestedSet.has(r.responsable);
                    return (
                      <button
                        key={r.responsable}
                        type="button"
                        onClick={() => toggle(r.responsable)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-neutral-50',
                          isChecked && 'bg-brand-50/60'
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                            isChecked
                              ? 'border-brand-600 bg-brand-600'
                              : 'border-neutral-300'
                          )}
                        >
                          {isChecked && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </span>
                        <span className="flex-1 truncate text-neutral-800">
                          {r.responsable}
                        </span>
                        {isSuggested && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                            <Sparkles className="h-2.5 w-2.5" />
                            Suggéré
                          </span>
                        )}
                        <span className="text-xs text-neutral-400">
                          {r.folderCount}{' '}
                          {r.folderCount > 1 ? 'dossiers' : 'dossier'}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-neutral-100 p-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMember.isPending}
            className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {updateMember.isPending ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
